import * as Sentry from '@sentry/react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import queryString from 'query-string';

import { ROUTES } from '@/configs/routes';
import { captureException } from '@/utils/captureException';
import { NEXT_URL_PARAM, saveNextUrl } from '@/utils/nextUrl';

import { ApiCallParams, ApiErrorResponse, BaseResponse, RequestOptions, RequestParams } from './types';

const AUTH_ENDPOINTS_PREFIX = '/api/auth/';

/** При 401 редирект на страницу авторизации, чтобы пользователь мог залогиниться заново. */
axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const requestUrl = error.config?.url ?? '';
    const isAuthEndpoint = requestUrl.startsWith(AUTH_ENDPOINTS_PREFIX);

    if (error.response?.status === 401 && !isAuthEndpoint) {
      const { pathname, search } = window.location;

      if (pathname !== ROUTES.root.path) {
        saveNextUrl(pathname + search);
        window.location.href = `${window.location.origin}${ROUTES.root.create()}?${NEXT_URL_PARAM}=${encodeURIComponent(pathname + search)}`;
      }
    }

    return Promise.reject(error);
  },
);

const callApi = async <ResponseData>({
  endpoint: url,
  method = 'GET',
  config = {},
  abortController,
}: ApiCallParams<ResponseData>): Promise<BaseResponse<ResponseData>> => {
  try {
    const response = await axios({
      url,
      method,
      baseURL: window.location.origin,
      signal: abortController?.signal,
      ...config,
    });

    const { data } = response.data;

    if (!data) {
      return {
        isError: false,
        data: response.data as ResponseData,
        paginator: response.data.paginator,
      };
    }

    return {
      isError: false,
      data,
      paginator: response.data.paginator,
    };
  } catch (error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    captureException(axiosError, 'api-call', {
      endpoint: url,
      method,
      status: axiosError.response?.status,
    });

    return {
      isError: true,
      error: axiosError,
    };
  }
};

const configApi = ({
  method = 'GET',
  data = {},
  config = {},
  multipartFormData = false,
}: RequestOptions): AxiosRequestConfig => {
  const queryConfig = { ...config };

  if ((queryConfig.data === null || queryConfig.data === undefined) && method.toUpperCase() !== 'GET') {
    queryConfig.data = data;
  }

  if (!queryConfig.headers) {
    queryConfig.headers = {};
  }

  if (multipartFormData) {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, item);
        });
      } else {
        formData.append(key, value);
      }
    });
    queryConfig.data = formData;

    Object.assign(queryConfig.headers, {
      'Content-Type': 'multipart/form-data',
    });
  }

  if (method.toUpperCase() === 'GET') {
    queryConfig.params = {
      ...data,
    };

    queryConfig.paramsSerializer = {
      ...queryConfig.paramsSerializer,
      serialize: (params) => queryString.stringify(params, { skipNull: true }),
    };
  }

  return queryConfig;
};

const call = async <ResponseData>(params: RequestParams<ResponseData>): Promise<BaseResponse<ResponseData>> => {
  const { endpoint, method, data, config, multipartFormData, mockResponse, abortController } = params;

  if (mockResponse) {
    return mockResponse;
  }

  try {
    return await callApi<ResponseData>({
      endpoint,
      method,
      config: configApi({ method, data, config, multipartFormData }),
      abortController,
    });
  } catch (err) {
    const error = err as AxiosError<ApiErrorResponse>;
    const errorStatus = error?.response?.status;
    const logMessage = `[API]: ${method} ${endpoint} END ERROR ${errorStatus}`;
    const errorData = error.response?.data || {};

    captureException(logMessage);

    Sentry.captureException(new Error(logMessage), {
      tags: {
        type: 'api',
        apiUrl: endpoint,
        apiStatus: errorStatus,
      },
      extra: {
        data: JSON.stringify(data),
        errorData: JSON.stringify(errorData),
      },
    });

    return {
      isError: true,
      error,
    };
  }
};

export default call;
